import i18n from "i18next";
import queryString from "query-string";

import "../i18n";

import { listing, payment } from "../constants/actionTypes";
import { listingScalarAutosaveFields } from "../constants/listings";
import { pickDefaultUpgradeOption } from "../helpers/upgradePlan";

const defaultSuccess = { addMembership: null, deleteMembership: null, downgradeSuccess: null, submitChanges: null };

const defaultModals = {
  addCard: false,
  editCard: false,
  upgrade: false,
  payPending: false,
  renew: false,
  payment: false,
  paymentSuccess: false,
  name: false,
  media: false,
  practiceArea: false,
  languages: false,
  bio: false,
  editLawFirm: false,
  lawFirmLocation: false,
  license: false,
  education: false,
  association: false,
  avvoReviews: false,
  yelpReviews: false,
  verification: false,
  verificationSuccess: false,
  listingBasics: false,
  insurances: false,
  confirm: false,
  delete: false,
  deleteSuccess: false,
  downgradePeriod: false,
  downgradePeriodSuccess: false,
  downgrade: false,
  downgradeSuccess: false,
  pause: false,
  pauseSuccess: false,
  unpause: false,
  unpauseSuccess: false,
  addPhone: false,
  addEmail: false,
  addPhoneSuccess: false,
  addEmailSuccess: false,
  deleteEmail: false,
  deleteEmailNew: false,
  deleteEmailSuccess: false,
  deletePhone: false,
  deletePhoneSuccess: false,
  addNewListing: false
};

const defaultState = {
  directoryPractices: [],
  listingAssistants: [],
  directoriesProfessions: { heritages: [] },
  practices: [],
  error: null,
  notificationTypesError: null,
  unsavedSections: {},
  // Per-row, per-field toggle pending: { [rowId]: { is_email: true, is_sms: true } }.
  // Field-scoped so an in-flight email PUT does not disable SMS (or the other seven switches).
  notificationTypePending: {},
  notificationTypeErrors: {},
  expand: "",
  memberData: {
    added: null,
    associations: [],
    insurances: [],
    attorney: null,
    description: "",
    educations: [],
    is_premium: null,
    licenses: [],
    listing_url: null,
    lawfirms: [],
    languages: [],
    verifiedphones: [],
    verifiedemails: [],
    notificationTypes: [],
    listingSectionHide: [],
    event_schedules: [],
    communities: [],
    event_tickets: [],
    yelp_disabled: "0",
    avvo_disabled: "0",
    phone: null,
    title: null,
    tags: [],
    bookmeetings: [],
    intro_video: "",
    jobs: [],
    parentCompany: [],
    education: [],
    practices: [],
    donationsSettings: null,
    donationsActivity: null,
    socials: [
      { social_name: "linkedin", social_url: "" },
      { social_name: "linkedinCompany", social_url: "" },
      { social_name: "facebook", social_url: "" },
      { social_name: "twitter", social_url: "" },
      { social_name: "instagram", social_url: "" },
      { social_name: "rss", social_url: "" },
      { social_name: "youtube", social_url: "" },
      { social_name: "yelp", social_url: "" }
    ],
    updated: null,
    website: null,
    directMessages: null
  },
  memberships: [],
  // The request the All Listings page loaded `memberships` with, kept for the reload after an action (GROWTH-7312).
  membershipsQuery: null,
  count: {},
  filters: {
    directories: [],
    freesponsor: [],
    professiongroups: []
  },
  preloaders: {
    addMembership: null,
    deleteMembership: null,
    downgrade: null,
    downgradePeriod: null,
    memberships: null,
    upgradeOptions: null,
    avvo: false,
    association: false,
    education: false,
    licenses: false,
    notificationTypes: false,
    directMessages: false,
    donationsSettings: false,
    donationsActivity: false,
    donationsRequest: false,
    donationsConnectStripe: false,
    donationsDisconnectStripe: false,
    listingSectionHide: false,
    updateNotificationType: false,
    updateDirectMessages: false,
    createNotificationType: false,
    memberData: null,
    modal: false,
    newMemberships: null,
    notificationEmail: null,
    yelp: false,
    photo: false,
    notifications: false,
    confirm: false,
    publishing: false,
    pause: false,
    unpause: false,
    listingAssistant: false,
    listingConfig: false,
    profile: true
  },
  socialGroups: [],
  insuranceGroups: {
    companies: [],
    networks: [],
    plans: []
  },
  success: defaultSuccess,
  photoError: null,
  upgradeOptions: [],
  showUpgradeOptionsError: false,
  upgradeOptionsError: null,
  validations: [],
  activeUpgradeOptions: {},
  notificationEmail: {
    isError: false,
    isErrorVerification: false,
    showVerification: false,
    canResend: false,
    email: "",
    token: ""
  },
  notificationPhone: {
    isError: false,
    isErrorVerification: false,
    showVerification: false,
    canResend: false,
    phone: "",
    token: ""
  },
  pagination: {
    currentPage: null,
    currentPageNum: null,
    lastPage: null,
    lastPageNum: null
  },
  modals: {
    ...defaultModals
  },
  savedPaymentData: {
    isActive: false
  },
  notificationModalData: "",
  languagesList: [],
  textLangDirectories: [],
  professions: [],
  listingConfig: {
    eventsProfession: "",
    jobsProfession: "",
    profilesForm: "",
    profilesProfession: ""
  },
  validationErrors: {},
  // AI Content Generation State
  aiContent: {
    currentJob: null, // { job_id, status, listing_id, directory_id, language_id, type }
    generatedContent: null, // Generated content to be applied to form
    loading: {
      generating: false,
      translating: false,
      jobStatus: false,
      content: false
    },
    error: null
  }
};

const deduplicateByKey = (arr, key) => {
  if (!Array.isArray(arr) || arr.length === 0) return arr;
  const seen = new Set();
  return arr.filter(item => {
    const id = item && item[key] != null ? String(item[key]) : "";
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const dropNotificationTypePendingFields = (pending, rowKey, fields = []) => {
  const rowPending = pending[rowKey];
  if (!rowPending) {
    return pending;
  }

  const nextRow = { ...rowPending };
  fields.forEach(field => {
    delete nextRow[field];
  });

  if (Object.keys(nextRow).length === 0) {
    const { [rowKey]: _dropped, ...rest } = pending;
    return rest;
  }

  return { ...pending, [rowKey]: nextRow };
};

const mergeTextLangDirectories = (helpDirectories = [], availabilityDirectories = [], fallbackDirectories = []) => {
  const dirsFromHelp = Array.isArray(helpDirectories) ? helpDirectories : [];
  const dirsFromAvailability = Array.isArray(availabilityDirectories) ? availabilityDirectories : [];
  const dirsFromFallback = Array.isArray(fallbackDirectories) ? fallbackDirectories : [];

  const publicationLanguagesByDirId = dirsFromAvailability.reduce((acc, item) => {
    const dirId = item?.dir_id;
    if (dirId === null || dirId === undefined || dirId === "") return acc;
    if (Array.isArray(item?.publication_languages) && item.publication_languages.length > 0) {
      acc[String(dirId)] = item.publication_languages;
    }
    return acc;
  }, {});

  if (dirsFromHelp.length > 0) {
    return dirsFromHelp.map(item => {
      const dirId = item?.dir_id ?? item?.listings_dir_id;
      const key = dirId !== null && dirId !== undefined ? String(dirId) : null;
      if (!key || !publicationLanguagesByDirId[key]) return item;
      return {
        ...item,
        publication_languages: publicationLanguagesByDirId[key]
      };
    });
  }

  if (dirsFromAvailability.length > 0) {
    return dirsFromAvailability;
  }

  return dirsFromFallback;
};

export default function(state = defaultState, action) {
  let newListings;
  const { payload, type } = action;
  switch (type) {
    case listing.SET_LOADING: {
      const isStartingPhotoRequest = payload.isSectionLoading && (payload.section === "photo" || payload.section === "parent_company");

      return {
        ...state,
        preloaders: { ...state.preloaders, [payload.section]: payload.isSectionLoading },
        ...(isStartingPhotoRequest ? { photoError: null } : {})
      };
    }

    case listing.SET_UNSAVED_SECTION: {
      const sectionKey = payload?.section;
      const listingScope = payload?.listingScope;
      if (!sectionKey || typeof listingScope !== "string") {
        return state;
      }

      const nextListingSections = { ...((state.unsavedSections || {})[listingScope] || {}) };
      if (!payload.status) {
        delete nextListingSections[sectionKey];
      } else {
        nextListingSections[sectionKey] = payload.status;
      }

      const nextUnsavedSections = { ...(state.unsavedSections || {}) };
      if (Object.keys(nextListingSections).length) {
        nextUnsavedSections[listingScope] = nextListingSections;
      } else {
        delete nextUnsavedSections[listingScope];
      }

      return {
        ...state,
        unsavedSections: nextUnsavedSections
      };
    }

    case listing.GET_MEMBER_DATA: {
      const incomingData = payload?.data || {};
      const currentMemberData = state.memberData || {};
      const mediaFields = [
        "primary_image_url",
        "cover_image_url",
        "cover_image_id",
        "listings_smp_cf_image_id",
        "listings_smp_cf_image_url",
        "additional_images"
      ];
      const newListingId = payload?.data?.listing_id;
      const prevListingId = state.memberData?.listing_id;
      const listingIdChanged =
        payload !== null &&
        newListingId !== undefined &&
        newListingId !== null &&
        prevListingId !== undefined &&
        prevListingId !== null &&
        String(newListingId) !== String(prevListingId);
      const shouldKeepExistingMediaValue = value =>
        value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0);
      const versionTransitionSectionFields = [
        "licenses",
        "languages",
        "insurances",
        "lawfirms",
        "socials",
        "education",
        "associations",
        "practices",
        "tags",
        "communities",
        "bookmeetings",
        "text_langs",
        "event_tickets",
        "event_schedules",
        "directories_availability",
        "jobs"
      ];
      const preserveMissingSectionData = (acc, fieldNames) =>
        fieldNames.reduce((result, fieldName) => {
          const incomingValue = result[fieldName];
          const existingValue = currentMemberData[fieldName];
          if (shouldKeepExistingMediaValue(incomingValue) && !shouldKeepExistingMediaValue(existingValue)) {
            return { ...result, [fieldName]: existingValue };
          }
          return result;
        }, acc);
      const normalizedIncomingData = listingIdChanged
        ? preserveMissingSectionData({ ...incomingData }, [...mediaFields, ...versionTransitionSectionFields])
        : mediaFields.reduce(
            (acc, fieldName) => {
              const incomingValue = acc[fieldName];
              const existingValue = currentMemberData[fieldName];
              if (shouldKeepExistingMediaValue(incomingValue) && !shouldKeepExistingMediaValue(existingValue)) {
                acc[fieldName] = existingValue;
              }
              return acc;
            },
            { ...incomingData }
          );
      const resolvedIncomingData = listingScalarAutosaveFields.reduce((acc, fieldName) => {
        if (Object.prototype.hasOwnProperty.call(incomingData, fieldName)) {
          return acc;
        }

        if (!listingIdChanged) {
          const existingValue = currentMemberData[fieldName];
          if (existingValue !== undefined && existingValue !== null && String(existingValue).length > 0) {
            acc[fieldName] = existingValue;
          }
        }

        return acc;
      }, normalizedIncomingData);
      const shouldResetDirectMessages = listingIdChanged;
      const shouldResetDonationsSettings = shouldResetDirectMessages;
      const mergedMemberData = shouldResetDirectMessages ? resolvedIncomingData : { ...state.memberData, ...resolvedIncomingData };
      const shouldResetDonationsActivity = shouldResetDirectMessages;

      return {
        ...state,
        notificationTypesError: listingIdChanged ? null : state.notificationTypesError,
        memberData:
          payload === null
            ? defaultState
            : {
                ...mergedMemberData,
                ...(Array.isArray(mergedMemberData.languages)
                  ? { languages: deduplicateByKey(mergedMemberData.languages, "language_id") }
                  : { languages: Array.isArray(state.memberData.languages) ? state.memberData.languages : [] }),
                ...(Array.isArray(mergedMemberData.practices) && {
                  practices: deduplicateByKey(mergedMemberData.practices, "practice_id")
                }),
                _profession: mergedMemberData._profession || state.memberData._profession,
                notificationTypes: listingIdChanged ? [] : state.memberData.notificationTypes,
                listingSectionHide: state.memberData.listingSectionHide || [],
                directMessages: shouldResetDirectMessages ? null : state.memberData.directMessages,
                donationsSettings: shouldResetDonationsSettings ? null : state.memberData.donationsSettings,
                donationsActivity: shouldResetDonationsActivity ? null : state.memberData.donationsActivity,
                directories_availability:
                  mergedMemberData.directories_availability &&
                  Array.isArray(mergedMemberData.directories_availability) &&
                  mergedMemberData.directories_availability.length > 0
                    ? mergedMemberData.directories_availability
                    : state.memberData?.directories_availability || []
              },
        success: { ...state.success, submitChanges: payload.successMessage },
        preloaders: {
          ...state.preloaders,
          licenses: false,
          educations: false,
          associations: false,
          avvo: false,
          yelp: false,
          modal: false,
          memberData: false,
          notifications: false,
          profile: false
        },
        modals: {
          ...defaultModals,
          practiceArea: state.modals.practiceArea,
          languages: state.modals.languages,
          paymentSuccess: state.modals.paymentSuccess,
          addPhoneSuccess: state.modals.addPhoneSuccess,
          addEmailSuccess: state.modals.addEmailSuccess,
          deleteEmailSuccess: state.modals.deleteEmailSuccess,
          deletePhoneSuccess: state.modals.deletePhoneSuccess,
          media: false
        },
        notificationEmail: {
          ...state.notificationEmail,
          showVerification: false,
          canResend: false,
          email: "",
          token: ""
        },
        notificationPhone: {
          ...state.notificationPhone,
          showVerification: false,
          canResend: false,
          phone: "",
          token: ""
        },
        validations: payload.help ? payload.help.validations : state.validations,
        socialGroups: payload.help ? payload.help.directory_socials : state.socialGroups,
        textLangDirectories: mergeTextLangDirectories(
          payload?.help?.text_lang_directories || payload?.data?.filling_options?.text_lang_directories,
          payload?.data?.directories_availability,
          state.textLangDirectories
        ),
        directoryPractices: payload.help ? Object.values(payload.help.practices) : state.directoryPractices,
        languagesList: payload.help ? Object.values(payload.help.languages) : state.languagesList,
        insuranceGroups: payload.help
          ? {
              companies: payload.help.insurances_companies,
              networks: payload.help.insurances_networks,
              plans: payload.help.insurances_plans
            }
          : state.insuranceGroups,
        // Keep other listings' maps: a GET for B must not drop A's in-flight or
        // failed status, and A's delayed autosave must not clear B (SEC-008).
        unsavedSections: payload === null ? {} : state.unsavedSections
      };
    }

    case listing.GET_VALIDATIONS: {
      return {
        ...state,
        expand: payload
      };
    }

    case listing.SET_DEFAULT_MEMBER_DATA: {
      return {
        ...state,
        memberData: defaultState.memberData,
        unsavedSections: {},
        preloaders: {
          ...state.preloaders,
          licenses: false,
          educations: false,
          associations: false,
          avvo: false,
          yelp: false,
          modal: false,
          memberData: false,
          notifications: false,
          profile: false
        }
      };
    }

    case listing.GET_MEMBER_DATA_FAIL: {
      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          licenses: false,
          educations: false,
          associations: false,
          avvo: false,
          yelp: false,
          modal: false,
          memberData: false,
          notifications: false,
          profile: false
        }
      };
    }

    case listing.SUBMIT_CHANGES_CLOSE_MODALS: {
      return {
        ...state,
        modals: {
          ...defaultModals
        }
      };
    }

    case listing.PUBLISH_LISTING: {
      const publishPatch = ["is_draft", "status", "listing_id", "edit_status", "change_status", "publish_status", "parent_id"].reduce(
        (acc, fieldName) => {
          if (Object.prototype.hasOwnProperty.call(payload || {}, fieldName)) {
            acc[fieldName] = payload[fieldName];
          }
          return acc;
        },
        {}
      );

      return {
        ...state,
        success: { ...state.success, submitChanges: true },
        preloaders: {
          ...state.preloaders,
          memberData: false
        },
        memberData: {
          ...state.memberData,
          ...publishPatch
        }
      };
    }

    case listing.SET_MEMBERSHIP_DATA_CLEAR:
      return {
        ...defaultState
      };

    case listing.PUBLISH_LISTING_FAIL:
      return {
        ...state,
        error: payload,
        preloaders: {
          ...state.preloaders,
          publishing: false
        }
      };

    case listing.SUBMIT_CHANGES_FAIL: {
      const failSection = action.section || (payload && payload.section);
      return {
        ...state,
        error: payload,
        preloaders: failSection ? { ...state.preloaders, [failSection]: false } : state.preloaders
      };
    }

    case listing.RESET_DRAFT_FAIL:
      return {
        ...state,
        error: payload,
        preloaders: {
          ...state.preloaders,
          memberData: false
        }
      };

    case listing.CLEAR_CHANGES_FIELD:
      return {
        ...state,
        success: defaultSuccess
      };

    case listing.DELETE_IMAGE_FAIL:
    case listing.DELETE_PRIMARY_IMAGE_FAIL:
    case listing.DELETE_COVER_IMAGE_FAIL: {
      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          photo: false
        },
        photoError: payload
      };
    }

    case listing.DELETE_ADDITIONAL_IMAGE: {
      return {
        ...state,
        memberData: {
          ...state.memberData,
          additional_images: state.memberData.additional_images.filter(item => String(item.image_id) !== String(payload))
        },
        preloaders: {
          ...state.preloaders,
          photo: false
        },
        photoError: null
      };
    }

    case listing.DELETE_PRIMARY_IMAGE: {
      return {
        ...state,
        memberData: {
          ...state.memberData,
          primary_image_url: "",
          listings_smp_cf_image_id: ""
        },
        preloaders: {
          ...state.preloaders,
          photo: false
        },
        photoError: null
      };
    }

    case listing.DELETE_COVER_IMAGE: {
      return {
        ...state,
        memberData: {
          ...state.memberData,
          listings_smp_cf_image_url: "",
          cover_image_url: "",
          cover_image_id: ""
        },
        preloaders: {
          ...state.preloaders,
          photo: false
        },
        photoError: null
      };
    }

    case listing.DELETE_PARENT_COVER_IMAGE: {
      const original = state.memberData;
      const clearParentImage = item => ({ ...item, image_url: "", cf_image_id: "" });

      return {
        ...state,
        memberData: {
          ...original,
          ...(Array.isArray(original.parent_company) && {
            parent_company: original.parent_company.map((item, index) => (index === 0 ? clearParentImage(item) : item))
          }),
          ...(Array.isArray(original.parentcompany) && {
            parentcompany: original.parentcompany.map((item, index) => (index === 0 ? clearParentImage(item) : item))
          }),
          ...(Array.isArray(original.parentCompany) && {
            parentCompany: original.parentCompany.map((item, index) => (index === 0 ? clearParentImage(item) : item))
          })
        },
        preloaders: {
          ...state.preloaders,
          photo: false
        },
        photoError: null
      };
    }

    case listing.SET_SUCCESS:
      return {
        ...state,
        success: { ...state.success, [payload.action]: payload.isActionSuccessful }
      };

    case listing.GET_MEMBERSHIPS: {
      // Support both numeric pagination (from API) and URL-based (legacy)
      const isNumericPage = typeof payload.currentPage === "number" && typeof payload.lastPage === "number";
      const lastPageNum =
        payload.isEvensOrJobsPage || isNumericPage
          ? payload.lastPage ?? 0
          : parseInt(queryString.parse(queryString.extract(payload.lastPage || "")).page, 10) || 0;
      const currentPageNum =
        payload.isEvensOrJobsPage || isNumericPage
          ? payload.currentPage ?? 1
          : parseInt(queryString.parse(queryString.extract(payload.currentPage || "")).page, 10) || 1;

      return {
        ...state,
        memberships: payload.listings,
        membershipsQuery: payload.query || null,
        count: { ...state.count, ...payload.count },
        filters: payload.filters,
        preloaders: { ...state.preloaders, memberships: false, deleteMembership: false },
        pagination: { currentPage: payload.currentPage, currentPageNum, lastPage: payload.lastPage, lastPageNum, perPage: payload.perPage }
      };
    }

    case listing.GET_MEMBERSHIPS_FAIL:
      return {
        ...state,
        preloaders: { ...state.preloaders, memberships: false, deleteMembership: false }
      };

    case listing.DELETE_SUCCESS: {
      const removedIndex = state.memberships.findIndex(({ listing_id }) => listing_id === payload);
      const newMemberships = [...state.memberships];
      newMemberships.splice(removedIndex, 1);

      return {
        ...state,
        memberships: newMemberships,
        modals: {
          ...defaultModals,
          delete: false,
          deleteSuccess: true
        },
        preloaders: {
          ...state.preloaders,
          deleteMembership: false
        }
      };
    }

    case listing.DELETE_FAIL:
      return {
        ...state,
        error: payload,
        preloaders: {
          ...state.preloaders,
          deleteMembership: false,
          memberData: false
        }
      };
    case listing.DOWNGRADE_SUCCESS: {
      newListings = state.memberships.map(item => {
        if (Number(item.listing_id) === Number(payload.id)) {
          return {
            ...item,
            // GROWTH-7414: what the listing becomes is the server's call, not the client's. A live listing stays Sponsor
            // until the paid period ends; the refetch every downgrade path dispatches brings the real answer.
            _canceled_to_date: payload.canceledDate,
            uncancel_ids: payload.unCancelIds
          };
        }

        return item;
      });

      return {
        ...state,
        memberships: newListings,
        modals: {
          ...defaultModals,
          downgrade: false,
          downgradePeriod: false,
          downgradeSuccess: true
        },
        preloaders: {
          ...state.preloaders,
          downgrade: false
        }
      };
    }

    case listing.UN_DOWNGRADE_SUCCESS:
      return {
        ...state,
        modals: {
          ...defaultModals,
          downgradePeriod: false,
          downgradePeriodSuccess: true
        },
        preloaders: {
          ...state.preloaders,
          downgrade: false
        }
      };

    case listing.UN_DOWNGRADE_FAIL:
      return {
        ...state,
        error: payload,
        preloaders: {
          ...state.preloaders,
          downgrade: false
        }
      };

    case listing.DOWNGRADE_FAIL:
      return {
        ...state,
        error: payload,
        preloaders: {
          ...state.preloaders,
          downgrade: false
        }
      };

    case listing.PAUSE_SUCCESS:
      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          pause: false
        },
        modals: {
          ...state.modals,
          pause: false,
          pauseSuccess: payload
        }
      };

    case listing.UNPAUSE_SUCCESS:
      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          unpause: false
        },
        modals: {
          ...state.modals,
          unpause: false,
          unpauseSuccess: payload
        }
      };

    case listing.PAUSE_FAIL:
      return {
        ...state,
        error: payload,
        preloaders: {
          ...state.preloaders,
          pause: false
        }
      };

    case listing.UNPAUSE_FAIL:
      return {
        ...state,
        error: payload,
        preloaders: {
          ...state.preloaders,
          unpause: false
        }
      };

    case listing.GET_UPGRADE_OPTIONS:
      return {
        ...state,
        upgradeOptions: payload,
        activeUpgradeOptions: pickDefaultUpgradeOption(payload),
        preloaders: { ...state.preloaders, upgradeOptions: false },
        showUpgradeOptionsError: false,
        upgradeOptionsError: null
      };

    case listing.GET_UPGRADE_OPTIONS_SHOW_ERROR:
      return {
        ...state,
        showUpgradeOptionsError: true,
        preloaders: { ...state.preloaders, upgradeOptions: false }
      };

    case listing.GET_UPGRADE_OPTIONS_HIDE_ERROR:
      return {
        ...state,
        showUpgradeOptionsError: false,
        upgradeOptionsError: null
      };

    case listing.UPDATE_ACTIVE_UPGRADE_OPTIONS:
      return {
        ...state,
        activeUpgradeOptions: payload
      };

    case listing.UPGRADE_CLEAN:
      return {
        ...state,
        upgradeOptions: [],
        upgradeOptionsError: null,
        showUpgradeOptionsError: false
      };

    case listing.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };

    case listing.CLEAR_PHOTO_ERROR:
      return {
        ...state,
        photoError: null
      };

    case listing.GET_UPGRADE_OPTIONS_FAIL:
      return {
        ...state,
        upgradeOptionsError: payload || i18n.t("dashboard_common_error_oops"),
        preloaders: { ...state.preloaders, upgradeOptions: false }
      };

    case payment.CHARGE_FOR_MEMBERSHIP_SUCCESS: {
      // GROWTH-7414: the product name is the one upgrade-options sent (meta.productName); without it (renewal, pending payment)
      // the name stays as the server gave it. Nothing here builds a name from the old one.
      const upgradedName = action.meta?.productName;
      const productPatch = upgradedName ? { product_name: upgradedName } : {};

      newListings = state.memberships.map(item => {
        if (Number(item.listing_id) === Number(payload)) {
          return { ...item, is_premium: true, is_free: false, ...productPatch, upgrade: "0" };
        }

        return item;
      });

      // memberData is the listing last opened in Listing Detail: patch it only when that is the listing charged.
      const isChargedListingOpen = state.memberData && Number(state.memberData.listing_id) === Number(payload);

      return {
        ...state,
        memberships: newListings,
        memberData: isChargedListingOpen
          ? { ...state.memberData, is_premium: true, is_free: false, ...productPatch, upgrade: "0" }
          : state.memberData
      };
    }
    case listing.SUBMIT_PHONE_EMAIL_SUCCESS:
      return {
        ...state,
        modals: {
          ...state.modals,
          deleteEmail: false,
          deletePhone: false,
          [payload]: true
        },
        notificationModalData: ""
      };
    case listing.TOGGLE_ACTIVITY_OF_MODAL:
      return {
        ...state,
        modals: {
          ...state.modals,
          [payload.modal]: payload.isActive
        },
        notificationModalData: ""
      };
    case listing.ACTIVE_NOTIFICATION_MODAL:
      return {
        ...state,
        modals: {
          ...state.modals,
          [payload.modal]: true
        },
        notificationModalData: payload.data
      };
    case listing.CLEAR_ALL_MODALS:
      return {
        ...state,
        modals: {
          ...defaultModals
        },
        photoError: null
      };
    case listing.NOTIFICATIONS_LOADING:
      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          notifications: payload
        },
        // Keep email/token and phone/token so a retry from the still-open modal
        // still posts the recipient. Wipe only on SUCCESS or RESET_NOTIFICATION_STATE.
        notificationEmail: {
          ...state.notificationEmail,
          isError: false,
          isErrorVerification: false,
          saveError: "",
          canResend: false
        },
        notificationPhone: {
          ...state.notificationPhone,
          isError: false,
          isErrorVerification: false,
          saveError: "",
          canResend: false
        }
      };
    case listing.NOTIFICATION_EMAIL:
      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          notifications: false
        },
        notificationEmail: {
          ...state.notificationEmail,
          showVerification: true,
          email: payload.email,
          token: payload.token || "",
          canResend: false
        }
      };
    case listing.NOTIFICATION_PHONE:
      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          notifications: false
        },
        notificationPhone: {
          ...state.notificationPhone,
          showVerification: true,
          phone: payload.phone,
          token: payload.token || "",
          isError: false,
          isErrorVerification: false,
          canResend: false
        }
      };

    case listing.NOTIFICATION_EMAIL_FAIL:
      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          notifications: false
        },
        notificationEmail: {
          ...state.notificationEmail,
          isError: payload
        }
      };
    case listing.NOTIFICATION_PHONE_FAIL:
      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          notifications: false
        },
        notificationPhone: {
          ...state.notificationPhone,
          isError: payload
        }
      };
    case listing.NOTIFICATION_VERIFICATION_EMAIL_FAIL:
      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          notifications: false
        },
        notificationEmail: {
          ...state.notificationEmail,
          isErrorVerification: payload?.saveError ? false : payload?.message || true,
          saveError: payload?.saveError || "",
          canResend: Boolean(payload?.canResend)
        }
      };
    case listing.NOTIFICATION_VERIFICATION_PHONE_FAIL:
      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          notifications: false
        },
        notificationPhone: {
          ...state.notificationPhone,
          isErrorVerification: payload?.saveError ? false : payload?.message || true,
          saveError: payload?.saveError || "",
          canResend: Boolean(payload?.canResend)
        }
      };
    case listing.NOTIFICATION_VERIFICATION_PHONE_SUCCESS:
      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          notifications: false
        },
        modals: {
          ...defaultModals,
          addPhone: false,
          addPhoneSuccess: true
        },
        notificationPhone: {
          ...state.notificationPhone,
          showVerification: false,
          canResend: false,
          phone: "",
          token: ""
        }
      };
    case listing.NOTIFICATION_VERIFICATION_EMAIL_SUCCESS:
      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          notifications: false
        },
        modals: {
          ...defaultModals,
          addEmail: false,
          addEmailSuccess: true
        },
        notificationEmail: {
          ...state.notificationEmail,
          showVerification: false,
          canResend: false,
          email: "",
          token: ""
        }
      };
    case listing.SET_SAVED_PAYMENT_DATA:
      return {
        ...state,
        savedPaymentData: {
          isActive: true,
          // GROWTH-7483: the plan modal to go back to; undefined when the caller names none (Pro Bono).
          planModal: payload
        }
      };
    case listing.CLEAR_SAVED_PAYMENT_DATA:
      return {
        ...state,
        savedPaymentData: {
          isActive: false
        }
      };

    case listing.GET_LISTING_ASSISTANT:
      return {
        ...state,
        listingAssistants: payload || []
      };

    case listing.GET_LISTINGS_CONFIG:
      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          listingConfig: false
        },
        listingConfig: payload
      };

    case listing.GET_LISTINGS_CONFIG_FAIL:
      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          listingConfig: false
        },
        error: payload
      };

    case listing.GET_DIRECTORIES_PROFESSIONS:
      return {
        ...state,
        directoriesProfessions: payload
      };

    case listing.SET_VALIDATION_ERROR:
      return {
        ...state,
        validationErrors: payload
      };

    case listing.RESET_NOTIFICATION_STATE:
      return {
        ...state,
        notificationEmail: {
          isError: false,
          isErrorVerification: false,
          saveError: "",
          canResend: false,
          showVerification: false,
          email: "",
          token: ""
        },
        notificationPhone: {
          isError: false,
          isErrorVerification: false,
          saveError: "",
          canResend: false,
          showVerification: false,
          phone: "",
          token: ""
        }
      };

    case listing.GET_NOTIFICATION_TYPES_SUCCESS:
      return {
        ...state,
        notificationTypesError: null,
        memberData: {
          ...state.memberData,
          notificationTypes: payload
        },
        preloaders: {
          ...state.preloaders,
          notificationTypes: false
        }
      };

    case listing.GET_NOTIFICATION_TYPES_FAIL:
      return {
        ...state,
        notificationTypesError: payload || true,
        preloaders: {
          ...state.preloaders,
          notificationTypes: false
        }
      };

    case listing.GET_DIRECT_MESSAGES_SUCCESS:
      return {
        ...state,
        memberData: {
          ...state.memberData,
          directMessages: payload
        },
        preloaders: {
          ...state.preloaders,
          directMessages: false
        }
      };

    case listing.GET_DIRECT_MESSAGES_FAIL:
      return {
        ...state,
        error: payload,
        preloaders: {
          ...state.preloaders,
          directMessages: false
        }
      };

    case listing.GET_DONATIONS_SETTINGS_SUCCESS:
      return {
        ...state,
        memberData: {
          ...state.memberData,
          donationsSettings: payload
        },
        preloaders: {
          ...state.preloaders,
          donationsSettings: false
        }
      };

    case listing.GET_DONATIONS_SETTINGS_FAIL:
      return {
        ...state,
        error: payload,
        memberData: {
          ...state.memberData,
          donationsSettings: {
            is_eligible: false,
            is_nonprofit: false,
            has_valid_license: false,
            settings_exists: false,
            settings: { donations_status: "not_requested" },
            _loadFailed: true
          }
        },
        preloaders: {
          ...state.preloaders,
          donationsSettings: false
        }
      };

    case listing.GET_DONATIONS_ACTIVITY_SUCCESS:
      return {
        ...state,
        memberData: {
          ...state.memberData,
          donationsActivity: payload
        },
        preloaders: {
          ...state.preloaders,
          donationsActivity: false
        }
      };

    case listing.GET_DONATIONS_ACTIVITY_FAIL:
      return {
        ...state,
        error: payload,
        preloaders: {
          ...state.preloaders,
          donationsActivity: false
        }
      };

    case listing.GET_LISTING_SECTION_HIDE_SUCCESS:
      return {
        ...state,
        memberData: {
          ...state.memberData,
          listingSectionHide: payload
        },
        preloaders: {
          ...state.preloaders,
          listingSectionHide: false
        }
      };

    case listing.GET_LISTING_SECTION_HIDE_FAIL:
      return {
        ...state,
        error: payload,
        preloaders: {
          ...state.preloaders,
          listingSectionHide: false
        }
      };

    case listing.UPDATE_LISTING_SECTION_HIDE_SUCCESS:
      return {
        ...state,
        memberData: {
          ...state.memberData,
          listingSectionHide: payload
        },
        preloaders: {
          ...state.preloaders,
          listingSectionHide: false
        }
      };

    case listing.UPDATE_LISTING_SECTION_HIDE_FAIL:
      return {
        ...state,
        error: payload,
        preloaders: {
          ...state.preloaders,
          listingSectionHide: false
        }
      };

    case listing.UPDATE_DIRECT_MESSAGES_SUCCESS:
      return {
        ...state,
        memberData: {
          ...state.memberData,
          directMessages: payload
        },
        preloaders: {
          ...state.preloaders,
          updateDirectMessages: false
        }
      };

    case listing.UPDATE_DIRECT_MESSAGES_FAIL:
      return {
        ...state,
        error: payload,
        preloaders: {
          ...state.preloaders,
          updateDirectMessages: false
        }
      };

    case listing.UPDATE_NOTIFICATION_TYPE: {
      const key = String(payload.id);
      const patch = payload.patch || {};
      const fieldPending = Object.keys(patch).reduce((acc, field) => ({ ...acc, [field]: true }), {});
      return {
        ...state,
        notificationTypePending: {
          ...state.notificationTypePending,
          [key]: {
            ...(state.notificationTypePending[key] || {}),
            ...fieldPending
          }
        },
        notificationTypeErrors: { ...state.notificationTypeErrors, [key]: null },
        memberData: {
          ...state.memberData,
          notificationTypes: (state.memberData.notificationTypes || []).map(item =>
            String(item.id) === key ? { ...item, ...patch } : item
          )
        }
      };
    }

    case listing.UPDATE_NOTIFICATION_TYPE_SUCCESS: {
      const { row, fields } = payload;
      const key = String(row.id);
      const merged = (fields || []).reduce((acc, field) => ({ ...acc, [field]: row[field] }), {});
      return {
        ...state,
        notificationTypePending: dropNotificationTypePendingFields(state.notificationTypePending, key, fields),
        memberData: {
          ...state.memberData,
          notificationTypes: (state.memberData.notificationTypes || []).map(item =>
            String(item.id) === key ? { ...item, ...merged } : item
          )
        },
        preloaders: {
          ...state.preloaders,
          updateNotificationType: false
        }
      };
    }

    case listing.UPDATE_NOTIFICATION_TYPE_ROLLBACK: {
      const key = String(payload.id);
      const fields = payload.fields || Object.keys(payload.previous || {});
      return {
        ...state,
        notificationTypePending: dropNotificationTypePendingFields(state.notificationTypePending, key, fields),
        notificationTypeErrors: { ...state.notificationTypeErrors, [key]: payload.error || true },
        memberData: {
          ...state.memberData,
          notificationTypes: (state.memberData.notificationTypes || []).map(item =>
            String(item.id) === key && payload.previous ? { ...item, ...payload.previous } : item
          )
        },
        preloaders: {
          ...state.preloaders,
          updateNotificationType: false
        }
      };
    }

    case listing.CREATE_NOTIFICATION_TYPE_SUCCESS: {
      const incoming = Array.isArray(payload) ? payload.filter(Boolean) : payload ? [payload] : [];
      const existing = Array.isArray(state.memberData.notificationTypes) ? state.memberData.notificationTypes : [];
      const byId = new Map();

      [...existing, ...incoming].forEach(item => {
        if (item && item.id !== undefined && item.id !== null) {
          byId.set(String(item.id), item);
        }
      });

      return {
        ...state,
        memberData: {
          ...state.memberData,
          notificationTypes: Array.from(byId.values())
        },
        preloaders: {
          ...state.preloaders,
          createNotificationType: false
        }
      };
    }

    case listing.CREATE_NOTIFICATION_TYPE_FAIL:
      return {
        ...state,
        error: payload,
        preloaders: {
          ...state.preloaders,
          createNotificationType: false
        }
      };

    // ============================================
    // AI Content Generation Cases
    // ============================================

    case listing.AI_GENERATE_DESCRIPTION:
      return {
        ...state,
        aiContent: {
          ...state.aiContent,
          loading: { ...state.aiContent.loading, generating: true },
          currentJob: null,
          error: null
        }
      };

    case listing.AI_GENERATE_DESCRIPTION_SUCCESS:
      return {
        ...state,
        aiContent: {
          ...state.aiContent,
          currentJob: {
            job_id: payload.job_id,
            status: payload.status,
            listing_id: payload.listing_id,
            directory_id: payload.directory_id,
            language_id: payload.language_id,
            type: "description"
          },
          loading: { ...state.aiContent.loading, generating: false },
          error: null
        }
      };

    case listing.AI_GENERATE_DESCRIPTION_FAIL:
      return {
        ...state,
        aiContent: {
          ...state.aiContent,
          loading: { ...state.aiContent.loading, generating: false },
          error: payload.message
        }
      };

    case listing.AI_GENERATE_TRANSLATION:
      return {
        ...state,
        aiContent: {
          ...state.aiContent,
          loading: { ...state.aiContent.loading, translating: true },
          currentJob: null,
          error: null
        }
      };

    case listing.AI_GENERATE_TRANSLATION_SUCCESS:
      return {
        ...state,
        aiContent: {
          ...state.aiContent,
          currentJob: {
            job_id: payload.job_id,
            status: payload.status,
            listing_id: payload.listing_id,
            directory_id: payload.directory_id,
            language_id: payload.language_id,
            type: "translation"
          },
          loading: { ...state.aiContent.loading, translating: false },
          error: null
        }
      };

    case listing.AI_GENERATE_TRANSLATION_FAIL:
      return {
        ...state,
        aiContent: {
          ...state.aiContent,
          loading: { ...state.aiContent.loading, translating: false },
          error: payload.message
        }
      };

    case listing.AI_GET_JOB_STATUS:
      return {
        ...state,
        aiContent: {
          ...state.aiContent,
          loading: { ...state.aiContent.loading, jobStatus: true }
        }
      };

    case listing.AI_GET_JOB_STATUS_SUCCESS:
      return {
        ...state,
        aiContent: {
          ...state.aiContent,
          currentJob: {
            ...state.aiContent.currentJob,
            status: payload.status,
            ...(payload.content && { generatedContent: payload.content })
          },
          loading: { ...state.aiContent.loading, jobStatus: false }
        }
      };

    case listing.AI_GET_JOB_STATUS_FAIL:
      return {
        ...state,
        aiContent: {
          ...state.aiContent,
          loading: { ...state.aiContent.loading, jobStatus: false },
          error: payload
        }
      };

    case listing.AI_GET_LISTING_CONTENT:
      return {
        ...state,
        aiContent: {
          ...state.aiContent,
          loading: { ...state.aiContent.loading, content: true }
        }
      };

    case listing.AI_GET_LISTING_CONTENT_SUCCESS:
      return {
        ...state,
        aiContent: {
          ...state.aiContent,
          generatedContent: payload,
          loading: { ...state.aiContent.loading, content: false }
        }
      };

    case listing.AI_GET_LISTING_CONTENT_FAIL:
      return {
        ...state,
        aiContent: {
          ...state.aiContent,
          loading: { ...state.aiContent.loading, content: false }
          // Don't set error - no content is valid state
        }
      };

    case listing.AI_SET_GENERATED_CONTENT:
      return {
        ...state,
        aiContent: {
          ...state.aiContent,
          generatedContent: payload
        }
      };

    case listing.AI_CLEAR_STATE:
      return {
        ...state,
        aiContent: {
          ...defaultState.aiContent
        }
      };

    default:
      return state;
  }
}
